// -----------------------------------------------------------------------------
// Discovery feature — DTOs + fetcher helpers (Phase 6 Wave 11.F, agent-ui).
//
// Mirrors the 11 endpoints under `/api/v1/document-types/discovery` plus the
// two new endpoints on `/api/v1/document-types/{slug}/recommendations` and
// `/api/v1/document-types/{slug}/reeducate`.
//
// Wire shapes mirror DocuSense.Application.Dtos.Discovery.* — fields stay in
// camelCase because the API serializes with that policy.
// -----------------------------------------------------------------------------

import { apiFetch } from '../../../api/client';
import { Endpoints } from '../../../api/types';
import type { ConfidenceThresholdsRequest, DocumentTypeResponse, FieldType } from '../../schema/api';

// ---------- Enums (stringified on the wire) ----------

export const DiscoveryStatuses = {
  Created: 'Created',
  Analyzing: 'Analyzing',
  ProposalReady: 'ProposalReady',
  InReview: 'InReview',
  Testing: 'Testing',
  ReadyToPublish: 'ReadyToPublish',
  Published: 'Published',
  Failed: 'Failed',
  Abandoned: 'Abandoned',
} as const;
export type DiscoveryStatus = (typeof DiscoveryStatuses)[keyof typeof DiscoveryStatuses];

export const CandidateFieldStatuses = {
  Proposed: 'Proposed',
  Accepted: 'Accepted',
  Modified: 'Modified',
  Rejected: 'Rejected',
} as const;
export type CandidateFieldStatus =
  (typeof CandidateFieldStatuses)[keyof typeof CandidateFieldStatuses];

export const CandidateChangeKinds = {
  Unchanged: 'Unchanged',
  New: 'New',
  Modified: 'Modified',
  RemovalSuggested: 'RemovalSuggested',
} as const;
export type CandidateChangeKind = (typeof CandidateChangeKinds)[keyof typeof CandidateChangeKinds];

export const DiscoverySteps = {
  Queued: 'Queued',
  ExtractingOcr: 'ExtractingOcr',
  AnalyzingFields: 'AnalyzingFields',
  CalculatingMaturity: 'CalculatingMaturity',
  GeneratingRecommendations: 'GeneratingRecommendations',
  PersistingProposal: 'PersistingProposal',
  Completed: 'Completed',
  Failed: 'Failed',
} as const;
export type DiscoveryStep = (typeof DiscoverySteps)[keyof typeof DiscoverySteps];

export const ConfidenceBuckets = {
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
} as const;
export type ConfidenceBucket = (typeof ConfidenceBuckets)[keyof typeof ConfidenceBuckets];

// ---------- Response DTOs ----------

export interface FieldEvidenceResponse {
  /** Label text only (e.g. "IBAN:") — never a document value. */
  nearbyLabel?: string | null;
  approximateLocation: string;
  pageNumber: number;
  appearedInSampleCount: number;
  totalSamples: number;
  isInTable: boolean;
  isKeyValuePair: boolean;
  /** 0..1 share of samples where the field was observed. */
  appearanceRate: number;
}

export interface CandidateTableColumnResponse {
  name: string;
  /** Scalar FieldType enum name; "Table" is never allowed for a column. */
  type: string;
  required: boolean;
  pattern?: string | null;
  hint?: string | null;
}

export interface CandidateFieldResponse {
  id: string;
  functionalName: string;
  description: string;
  suggestedType: string; // FieldType enum name
  suggestedRequired: boolean;
  isCritical: boolean;
  aliases: string[];
  evidence: FieldEvidenceResponse;
  confidence: string; // ConfidenceBucket
  suggestionReason: string;
  risks: string[];
  status: string; // CandidateFieldStatus
  isTableField: boolean;
  /** Editable AI hint surfaced to the extraction prompt for this field. */
  hint?: string | null;
  /** Column declarations when isTableField=true. null/undefined for non-table fields. */
  columns?: CandidateTableColumnResponse[] | null;
  /**
   * Typed child candidates when suggestedType === "Group". null/undefined for non-group
   * candidates. Children themselves are never groups (V1 cap enforced server-side).
   */
  children?: CandidateFieldResponse[] | null;
  /**
   * How this candidate differs from the source schema (only meaningful for re-education drafts).
   * Null/undefined for vanilla discovery drafts.
   */
  changeKind?: string | null; // CandidateChangeKind
}

export interface MaturityResponse {
  score: number; // 0..100
  canPublish: boolean;
  samplesProcessed: number;
  fieldsWithHighConfidence: number;
  totalCandidateFields: number;
  warnings: string[];
  blockers: string[];
}

export interface DiscoveryRecommendationResponse {
  type: string;
  affectedFieldName?: string | null;
  message: string;
  actionableHint: string;
  isBlocker: boolean;
}

export interface DiscoveryProgressResponse {
  /** Stringified DiscoveryStep enum. */
  currentStep: string;
  /** 0..100. */
  percentComplete: number;
  /** Optional short human-readable detail (e.g. "OCR 1/3"). */
  lastMessage?: string | null;
  lastUpdatedAt: string;
}

export interface IntelligentDraftResponse {
  id: string;
  tenantId: string;
  status: string; // DiscoveryStatus
  suggestedName?: string | null;
  suggestedCategory?: string | null;
  sampleCount: number;
  processedSampleCount: number;
  candidateFields: CandidateFieldResponse[];
  maturity: MaturityResponse;
  recommendations: DiscoveryRecommendationResponse[];
  publishedDocumentTypeSlug?: string | null;
  createdAt: string;
  updatedAt: string;
  progress: DiscoveryProgressResponse;
  failureReason?: string | null;
  /** Provider id the user chose at draft creation (or null when defaulted). */
  chosenModelProviderId?: string | null;
  /**
   * Non-null when this draft is re-educating an existing type. Publishing will create a NEW VERSION
   * of that type instead of a new document type.
   */
  sourceDocumentTypeSlug?: string | null;
  /** Number of golden samples (reviewer-corrected production documents) used in re-education analysis. */
  goldenSamplesUsed?: number;
}

export interface IntelligentDraftSummaryResponse {
  id: string;
  suggestedName?: string | null;
  status: string;
  sampleCount: number;
  maturityScore: number;
  updatedAt: string;
}

export interface ImprovementRecommendationResponse {
  /** Backing signal id for the apply/dismiss endpoints; empty for the synthetic OcrTierEscalation card. */
  signalId: string;
  documentTypeSlug: string;
  fieldName: string;
  signalType: string;
  occurrenceCount: number;
  message: string;
  proposedAction: string;
  /**
   * Pre-filled suggested hint for the apply modal, derived from the signal type and the field's
   * schema metadata (never from document values). Empty for the synthetic OcrTierEscalation card.
   */
  proposedHint: string;
  /**
   * Fix affordance: 'Hint' (set a reviewed hint), 'Pattern' (the field's regex needs review), or
   * 'None' (type-level recommendation, e.g. OcrTierEscalation).
   */
  proposedKind: 'Hint' | 'Pattern' | 'None';
}

// ---------- Request DTOs ----------

export interface StartDiscoveryRequest {
  sampleBlobPaths: string[];
  hintName?: string | null;
  hintCategory?: string | null;
  /**
   * Optional provider id the user picked from the tenant's configured providers.
   * When omitted the backend falls back to the default-selection logic.
   */
  modelProviderId?: string | null;
}

export interface AddSampleBody {
  /**
   * Batched on purpose: a multi-file upload must land as ONE backend call so the analysis starts
   * with the complete sample set and a single orchestration trigger.
   */
  sampleBlobPaths: string[];
}

export interface CandidateTableColumnRequest {
  name: string;
  type: FieldType;
  required: boolean;
  pattern?: string | null;
  hint?: string | null;
}

export interface ModifyCandidateFieldRequest {
  functionalName?: string | null;
  suggestedType?: FieldType | null;
  suggestedRequired?: boolean | null;
  isCritical?: boolean | null;
  /**
   * Optional replacement hint. When provided (even as empty string, meaning "clear")
   * the backend overwrites the hint without flipping the field to Modified.
   */
  hint?: string | null;
  /**
   * Optional replacement columns. When provided the backend overwrites the column list
   * verbatim (empty array clears all columns) without flipping the field to Modified.
   * Only valid for table fields.
   */
  columns?: CandidateTableColumnRequest[] | null;
}

export interface DiscoveryRawAiResponse {
  draftId: string;
  rawAiJson?: string | null;
}

export interface PublishFromDraftRequest {
  finalName: string;
  finalSlug: string;
  modelProviderId: string;
  thresholds?: ConfidenceThresholdsRequest | null;
  forcePublishDespiteWarnings: boolean;
}

// ---------- Endpoint paths ----------

const DISCOVERY_BASE = `${Endpoints.documentTypes}/discovery`;

function draftUrl(draftId: string): string {
  return `${DISCOVERY_BASE}/${encodeURIComponent(draftId)}`;
}

function fieldUrl(draftId: string, fieldId: string): string {
  return `${draftUrl(draftId)}/fields/${encodeURIComponent(fieldId)}`;
}

// ---------- Fetchers ----------

export function startDiscovery(
  request: StartDiscoveryRequest,
): Promise<IntelligentDraftResponse> {
  return apiFetch<IntelligentDraftResponse>(DISCOVERY_BASE, {
    method: 'POST',
    json: request,
  });
}

export function listDiscoveryDrafts(
  signal?: AbortSignal,
): Promise<IntelligentDraftSummaryResponse[]> {
  return apiFetch<IntelligentDraftSummaryResponse[]>(
    DISCOVERY_BASE,
    signal !== undefined ? { signal } : {},
  );
}

export function getDiscoveryDraft(
  draftId: string,
  signal?: AbortSignal,
): Promise<IntelligentDraftResponse> {
  return apiFetch<IntelligentDraftResponse>(
    draftUrl(draftId),
    signal !== undefined ? { signal } : {},
  );
}

export function addDiscoverySample(
  draftId: string,
  body: AddSampleBody,
): Promise<IntelligentDraftResponse> {
  return apiFetch<IntelligentDraftResponse>(`${draftUrl(draftId)}/samples`, {
    method: 'POST',
    json: body,
  });
}

export function acceptCandidateField(
  draftId: string,
  fieldId: string,
): Promise<IntelligentDraftResponse> {
  return apiFetch<IntelligentDraftResponse>(`${fieldUrl(draftId, fieldId)}/accept`, {
    method: 'PUT',
  });
}

export function rejectCandidateField(
  draftId: string,
  fieldId: string,
): Promise<IntelligentDraftResponse> {
  return apiFetch<IntelligentDraftResponse>(`${fieldUrl(draftId, fieldId)}/reject`, {
    method: 'PUT',
  });
}

export function modifyCandidateField(
  draftId: string,
  fieldId: string,
  request: ModifyCandidateFieldRequest,
): Promise<IntelligentDraftResponse> {
  return apiFetch<IntelligentDraftResponse>(fieldUrl(draftId, fieldId), {
    method: 'PUT',
    json: request,
  });
}

export function getDraftMaturity(
  draftId: string,
  signal?: AbortSignal,
): Promise<MaturityResponse> {
  return apiFetch<MaturityResponse>(
    `${draftUrl(draftId)}/maturity`,
    signal !== undefined ? { signal } : {},
  );
}

export function publishDraft(
  draftId: string,
  request: PublishFromDraftRequest,
): Promise<DocumentTypeResponse> {
  return apiFetch<DocumentTypeResponse>(`${draftUrl(draftId)}/publish`, {
    method: 'POST',
    json: request,
  });
}

export function listImprovementSignals(
  slug: string,
  signal?: AbortSignal,
): Promise<ImprovementRecommendationResponse[]> {
  return apiFetch<ImprovementRecommendationResponse[]>(
    `${Endpoints.documentTypes}/${encodeURIComponent(slug)}/recommendations`,
    signal !== undefined ? { signal } : {},
  );
}

/** Applies a surfaced suggestion: sets the reviewed hint on the field and marks the signal applied. */
export function applyRecommendation(
  slug: string,
  signalId: string,
  hint: string,
): Promise<DocumentTypeResponse> {
  return apiFetch<DocumentTypeResponse>(
    `${Endpoints.documentTypes}/${encodeURIComponent(slug)}/recommendations/${encodeURIComponent(signalId)}/apply`,
    { method: 'POST', json: { hint } },
  );
}

/** Dismisses (ignores) a surfaced improvement signal without changing the schema. */
export function dismissRecommendation(slug: string, signalId: string): Promise<void> {
  return apiFetch<void>(
    `${Endpoints.documentTypes}/${encodeURIComponent(slug)}/recommendations/${encodeURIComponent(signalId)}/dismiss`,
    { method: 'POST' },
  );
}

export function reeducateDocumentType(
  slug: string,
  modelProviderId?: string | null,
): Promise<IntelligentDraftResponse> {
  return apiFetch<IntelligentDraftResponse>(
    `${Endpoints.documentTypes}/${encodeURIComponent(slug)}/reeducate`,
    {
      method: 'POST',
      // null = "use the type's own configured model" (backend smart default).
      json: { modelProviderId: modelProviderId ?? null },
    },
  );
}

// ---------- Deferral D4 — SAS direct upload ----------

export interface DiscoveryUploadTarget {
  sasUri: string;
  blobPath: string;
  expiresAt: string;
}

export interface IssueDiscoverySasResponse {
  draftId: string;
  isPreliminary: boolean;
  targets: DiscoveryUploadTarget[];
}

export interface IssueDiscoverySasBody {
  count: number;
}

/** Mints SAS URIs against a preliminary draft id (no draft exists yet). */
export function issuePreliminaryDiscoverySas(
  count: number,
): Promise<IssueDiscoverySasResponse> {
  return apiFetch<IssueDiscoverySasResponse>(`${DISCOVERY_BASE}/sas`, {
    method: 'POST',
    json: { count } satisfies IssueDiscoverySasBody,
  });
}

/** Mints SAS URIs scoped to an existing draft. */
export function issueDiscoverySasForDraft(
  draftId: string,
  count: number,
): Promise<IssueDiscoverySasResponse> {
  return apiFetch<IssueDiscoverySasResponse>(`${draftUrl(draftId)}/sas`, {
    method: 'POST',
    json: { count } satisfies IssueDiscoverySasBody,
  });
}

// ---------- Deferral D6 — abandon draft ----------

export function abandonDraft(draftId: string): Promise<IntelligentDraftResponse> {
  return apiFetch<IntelligentDraftResponse>(`${draftUrl(draftId)}/abandon`, {
    method: 'POST',
  });
}

// ---------- Platform debug — raw AI JSON (requires platform:debug scope) ----------

/**
 * Fetches the verbatim AI response stored on the draft. Endpoint is gated by the
 * `platform:debug` scope — non-superuser principals get 403, which callers should
 * translate into "hide the tab" rather than show an error.
 */
export function getDiscoveryRawAiResponse(
  draftId: string,
  signal?: AbortSignal,
): Promise<DiscoveryRawAiResponse> {
  return apiFetch<DiscoveryRawAiResponse>(
    `${draftUrl(draftId)}/raw-ai-json`,
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Query keys ----------

export const DiscoveryQueryKeys = {
  all: ['discovery'] as const,
  drafts: () => [...DiscoveryQueryKeys.all, 'drafts'] as const,
  draft: (draftId: string) => [...DiscoveryQueryKeys.all, 'draft', draftId] as const,
  maturity: (draftId: string) => [...DiscoveryQueryKeys.all, 'maturity', draftId] as const,
  signals: (slug: string) => [...DiscoveryQueryKeys.all, 'signals', slug] as const,
  rawAi: (draftId: string) => [...DiscoveryQueryKeys.all, 'raw-ai', draftId] as const,
};
